import { Users, Phone, Heart, Scale, Home, Shield } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const ChildCustody = () => {
  return (
    <GenericPracticePage
      title="Child Custody Attorney"
      subtitle="Fighting for your parental rights and your child's best interests. Experienced representation in custody disputes, modifications, and enforcement throughout Texas."
      icon={<Users className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />}
      mainContent={
        <div className="space-y-8">
          <section>
            <h2 className="text-3xl font-bold mb-4">Texas Child Custody Lawyer</h2>
            <p className="text-lg text-muted-foreground mb-4">
              Your relationship with your children is the most important thing in the world. When custody is at stake, you need an attorney who will fight tirelessly to protect your parental rights and ensure your children's wellbeing. We understand the emotional and legal complexities of custody cases and provide compassionate yet aggressive representation.
            </p>
            <p className="text-lg text-muted-foreground">
              Whether you're going through divorce, need to modify an existing order, or face an interstate custody dispute, we have the experience to achieve the best possible outcome for you and your children.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Understanding Texas Conservatorship</h2>
            <p className="text-muted-foreground mb-4">
              In Texas, "custody" is legally called "conservatorship." There are two main types:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <Scale className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Joint Managing Conservatorship (JMC)</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Both parents share decision-making rights regarding the child's education, medical care, and religious upbringing. This is the preferred arrangement in Texas unless family violence or other serious issues exist.
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> Joint conservatorship doesn't necessarily mean equal time. One parent is typically designated the primary residence.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <Shield className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Sole Managing Conservatorship (SMC)</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  One parent has exclusive right to make major decisions about the child. The other parent becomes a "possessory conservator" with visitation rights but limited decision-making authority.
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Granted when:</strong> Family violence, neglect, drug abuse, or when joint conservatorship would not be in the child's best interest.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Factors Courts Consider</h2>
            <div className="bg-gradient-subtle p-6 rounded-lg">
              <p className="text-muted-foreground mb-4">
                Texas courts determine custody based on the "best interest of the child" standard. Key factors include:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">• <strong>Child's emotional and physical needs</strong></p>
                  <p className="text-sm text-muted-foreground">• <strong>Parental abilities</strong> to meet child's needs</p>
                  <p className="text-sm text-muted-foreground">• <strong>Stability</strong> of home environment</p>
                  <p className="text-sm text-muted-foreground">• <strong>Plans for the child</strong> by each parent</p>
                  <p className="text-sm text-muted-foreground">• <strong>Parental cooperation</strong> and co-parenting ability</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">• <strong>Geographic proximity</strong> of parents</p>
                  <p className="text-sm text-muted-foreground">• <strong>Child's preference</strong> (if 12 or older)</p>
                  <p className="text-sm text-muted-foreground">• <strong>History of family violence</strong></p>
                  <p className="text-sm text-muted-foreground">• <strong>Substance abuse</strong> issues</p>
                  <p className="text-sm text-muted-foreground">• <strong>Each parent's involvement</strong> in child's life</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Possession & Access (Visitation)</h2>
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">Standard Possession Order (SPO)</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Texas has a standard visitation schedule that applies unless parents agree otherwise or special circumstances exist:
                </p>
                <div className="bg-accent/10 p-4 rounded">
                  <p className="text-sm mb-2"><strong>During School Year:</strong></p>
                  <ul className="text-sm space-y-1 text-muted-foreground ml-4">
                    <li>• 1st, 3rd, and 5th weekends of each month (Friday evening to Sunday evening)</li>
                    <li>• Thursday evenings during school year</li>
                    <li>• Spring break in even years</li>
                  </ul>
                  <p className="text-sm mt-3 mb-2"><strong>Summer:</strong></p>
                  <ul className="text-sm space-y-1 text-muted-foreground ml-4">
                    <li>• 30 days designated by non-custodial parent</li>
                    <li>• Primary parent gets one weekend per month during non-custodial parent's summer possession</li>
                  </ul>
                  <p className="text-sm mt-3 mb-2"><strong>Holidays alternate yearly</strong></p>
                </div>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">Expanded Standard Possession (for parents within 50 miles)</h3>
                <p className="text-sm text-muted-foreground">
                  Additional Thursday overnight visits and longer weekend visits. More time for the non-custodial parent when parents live close together.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">Customized Possession Schedules</h3>
                <p className="text-sm text-muted-foreground">
                  Parents can agree to different schedules: 50/50 time splits, week-on/week-off, 2-2-5-5, or any arrangement that works for the family. Courts will approve if it's in the child's best interest.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Custody Modification</h2>
            <div className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
              <p className="text-muted-foreground mb-4">
                Custody orders can be modified if circumstances have substantially changed since the last order, and modification is in the child's best interest.
              </p>
              <p className="font-bold mb-2">Common Reasons for Modification:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                <p className="text-sm text-muted-foreground">• Parent's relocation</p>
                <p className="text-sm text-muted-foreground">• Child's changing needs as they age</p>
                <p className="text-sm text-muted-foreground">• Substantial change in parent's circumstances</p>
                <p className="text-sm text-muted-foreground">• Parent's failure to follow court orders</p>
                <p className="text-sm text-muted-foreground">• New evidence of endangerment</p>
                <p className="text-sm text-muted-foreground">• Child's preference (if 12+)</p>
              </div>
              <p className="text-sm text-muted-foreground">
                <strong>Important:</strong> You generally cannot file for modification until at least one year after the previous order, unless the child's present environment endangers their wellbeing.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Enforcement of Custody Orders</h2>
            <div className="bg-destructive/10 border border-destructive/20 p-6 rounded-lg">
              <p className="text-muted-foreground mb-4">
                If the other parent violates the custody order by withholding visitation, failing to return the child, or interfering with your parental rights, we can file an enforcement action.
              </p>
              <p className="text-muted-foreground mb-4"><strong>Consequences for violating custody orders include:</strong></p>
              <div className="space-y-2 mb-4">
                <p className="text-sm text-muted-foreground">• Contempt of court (fines and jail time)</p>
                <p className="text-sm text-muted-foreground">• Make-up possession time</p>
                <p className="text-sm text-muted-foreground">• Attorney's fees</p>
                <p className="text-sm text-muted-foreground">• Modification of custody arrangement</p>
              </div>
              <p className="text-muted-foreground font-semibold">
                Don't let violations continue. Enforcing your rights protects your relationship with your child.
              </p>
            </div>
          </section>

          <section className="text-center bg-accent/5 p-8 rounded-lg">
            <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-2xl font-bold mb-4">Fighting for Your Parental Rights?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Contact us today for experienced child custody representation. We'll fight to protect your relationship with your children.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059" className="text-2xl font-bold text-accent hover:text-accent/80">
                (832) 930-3059
              </a>
            </div>
          </section>
        </div>
      }
    />
  );
};

export default ChildCustody;
